// client/src/hooks/usePages.js
import { useState, useCallback, useEffect } from 'react';
import api from '../services/api';
import useAlert from './useAlert'; // Para um feedback melhor ao usuário

export const usePages = () => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setAlert } = useAlert();

    // ✅ AJUSTADO: Esta função agora busca TODAS as páginas para o painel de ADMIN.
    const fetchPages = useCallback(async () => {
        setLoading(true);
        try {
            // ✅ CORREÇÃO: Usa a rota de ADMIN para buscar as páginas.
            const response = await api.get('/admin/institucional/pages');
            // A resposta do advancedResults já vem em 'data', não 'data.data'
            setPages(response.data.data); 
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao buscar páginas';
            setError(msg);
            setAlert(msg, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    // ✅ AJUSTADO: Busca uma única página pelo ID para o ADMIN.
    const getPage = useCallback(async (pageId) => {
        try {
            // ✅ CORREÇÃO: Usa a rota de ADMIN.
            const response = await api.get(`/admin/institucional/pages/${pageId}`);
            return response.data;
        } catch (err) {
            throw new Error(err.response?.data?.error || err.response?.data?.message || 'Erro ao buscar página');
        }
    }, []);

    // ❌ REMOVIDO: A rota /type/:pageType foi removida do backend, então esta função é obsoleta.
    // const getPageByType = ...

    // ✅ MANTIDO: Esta função é para o site PÚBLICO, então o caminho está correto.
    const getPageBySlug = useCallback(async (slug) => {
        try {
            const response = await api.get(`/institucional/pages/slug/${slug}`);
            return response.data;
        } catch (err) {
            if (err.response?.status === 404) {
                return { data: null };
            }
            throw new Error(err.response?.data?.error || err.response?.data?.message || 'Erro ao buscar página pelo slug');
        }
    }, []);

    // ✅ AJUSTADO: Cria uma página usando a rota de ADMIN.
    const createPage = useCallback(async (pageData) => {
        try {
            // ✅ CORREÇÃO: Usa a rota de ADMIN.
            const response = await api.post('/admin/institucional/pages', pageData);
            setPages(prev => [...prev, response.data.data]);
            setAlert('Página criada com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao criar página';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    // ✅ AJUSTADO: Atualiza uma página usando a rota de ADMIN.
    const updatePage = useCallback(async (id, pageData) => {
        try {
            // ✅ CORREÇÃO: Usa a rota de ADMIN.
            const response = await api.put(`/admin/institucional/pages/${id}`, pageData);
            setPages(prev => prev.map(page => 
                page._id === id ? response.data.data : page
            ));
            setAlert('Página atualizada com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar página';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    // ✅ AJUSTADO: Deleta uma página usando a rota de ADMIN.
    const deletePage = useCallback(async (id) => {
        try {
            // ✅ CORREÇÃO: Usa a rota de ADMIN.
            await api.delete(`/admin/institucional/pages/${id}`);
            setPages(prev => prev.filter(page => page._id !== id));
            setAlert('Página excluída com sucesso!', 'success');
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao deletar página';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    // ✅ AJUSTADO: Publica uma página usando a rota de ADMIN.
    const publishPage = useCallback(async (id) => {
        try {
            // ✅ CORREÇÃO: Usa a rota de ADMIN.
            const response = await api.put(`/admin/institucional/pages/${id}/publish`);
            setPages(prev => prev.map(page => 
                page._id === id ? response.data.data : page
            ));
            setAlert('Página publicada com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao publicar página';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    // ✅ AJUSTADO: Reordena blocos usando a rota de ADMIN.
    const reorderContentBlocks = useCallback(async (id, blockIds) => {
        try {
            // ✅ CORREÇÃO: Usa a rota de ADMIN.
            const response = await api.put(`/admin/institucional/pages/${id}/reorder`, { blockIds });
            setPages(prev => prev.map(page => 
                page._id === id ? response.data.data : page
            ));
            setAlert('Ordem dos blocos atualizada!', 'success');
            return response.data;
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao reordenar blocos';
            setAlert(msg, 'error');
            throw new Error(msg);
        }
    }, [setAlert]);

    useEffect(() => {
        fetchPages();
    }, [fetchPages]);

    return {
        pages,
        loading,
        error,
        getPage,
        // getPageByType, // ❌ Removido
        getPageBySlug,
        createPage,
        updatePage,
        deletePage,
        publishPage,
        reorderContentBlocks,
        refetch: fetchPages
    };
};